import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DatePicker } from "../DatePicker";
import {
  ExpenseTypePickerField,
  GroupPickerField,
  SourcePickerField,
} from "../EntityPickerField";
import { useRegistry } from "../../context/RegistryContext";
import { useActionSets } from "../../api/queries";
import {
  type AutoTransactionRuleDto,
  type EditActionDto,
  type FieldName,
  type DateString,
  type AutoTxId,
  type SourceId,
  type GroupId,
  type ExpenseTypeId,
  makeDateString,
} from "../../api/types";
import { makeMoney } from "../../logic/money";
import type { StoredTransaction } from "../../logic/derivedAccount";
import {
  type CollectedPrompt,
  collectPrompts,
  promptLabels,
  renderPrefix,
} from "./prompts";

export type PromptRuleDialogProps = {
  open: boolean;
  rule: AutoTransactionRuleDto | null;
  transaction: StoredTransaction | null;
  onCancel: () => void;
  onConfirm: (actions: EditActionDto[]) => void;
};

const FIELD_LABELS: Record<FieldName, string> = {
  source: "Source",
  merchant: "Merchant",
  description: "Description",
  group: "Group",
  amount: "Amount",
  expenseType: "Expense Type",
  datePaid: "Date Paid",
  // Read-only import fields — never promptable, present only for exhaustiveness.
  kid: "KID",
  payToAccount: "Pay To Account",
};

// For entity-valued prompts, the state holds an id (or empty string while
// unset); name/free-text prompts hold a string; amount holds the raw decimal
// string from the number input, converted to `Money` when the action is built.
type PromptValue = string;

const EMPTY_ID = "" as never;

const buildAction = (field: FieldName, value: PromptValue): EditActionDto => {
  switch (field) {
    case "source":
      return { field: "source", value: value as SourceId };
    case "merchant":
      return { field: "merchant", value: value as string };
    case "description":
      return { field: "description", value: value as string };
    case "group":
      return { field: "group", value: value as GroupId };
    case "amount":
      return { field: "amount", value: makeMoney(value) };
    case "expenseType":
      return { field: "expenseType", value: value as ExpenseTypeId };
    case "datePaid":
      return { field: "datePaid", value: value as DateString };
    case "kid":
    case "payToAccount":
      // Import-sourced, read-only: no edit action exists for these, and they
      // are never offered as prompt targets.
      throw new Error(`${field} is read-only and cannot be set by a rule`);
  }
};

export const PromptRuleDialog = ({
  open,
  rule,
  transaction,
  onCancel,
  onConfirm,
}: PromptRuleDialogProps) => {
  // Expanded, so a prompt living inside a cited action set is asked for too.
  const { data: sets } = useActionSets();
  const prompts = useMemo(() => collectPrompts(rule, sets), [rule, sets]);

  return open && rule && transaction && prompts.length > 0 ? (
    <PromptRuleDialogInner
      key={`${rule.id}-${transaction.id}`}
      rule={rule}
      transaction={transaction}
      prompts={prompts}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  ) : null;
};

type InnerProps = {
  rule: AutoTransactionRuleDto;
  transaction: StoredTransaction;
  prompts: CollectedPrompt[];
  onCancel: () => void;
  onConfirm: (actions: EditActionDto[]) => void;
};

/** Puts the caret after a seeded prefix so the user types straight on. */
const caretToEnd = (e: React.FocusEvent<HTMLInputElement>) => {
  const { value } = e.target;
  e.target.setSelectionRange(value.length, value.length);
};

const PromptRuleDialogInner = ({
  rule,
  transaction,
  prompts,
  onCancel,
  onConfirm,
}: InnerProps) => {
  const { sourceMap, groupMap, expenseTypeMap } = useRegistry();
  const labels = promptLabels(prompts, (field) => FIELD_LABELS[field]);

  // Seed entity-valued prompts with the current transaction's id (resolved
  // from its name via the registry); free-text/number prompts hold the raw
  // value. The map keys are names; `?.id` falls back to "" if the registry
  // hasn't loaded the entity yet. A rule-supplied prefix wins over the
  // transaction's current text — it is the lead-in the user is meant to
  // complete, not something to append to what's already there.
  const initialValueFor = ({
    spec: { field, prefix },
  }: CollectedPrompt): PromptValue => {
    const seed = prefix === null ? null : renderPrefix(prefix, transaction);
    switch (field) {
      case "source":
        return sourceMap[transaction.source]?.id ?? (EMPTY_ID as SourceId);
      case "merchant":
        return seed ?? transaction.merchant;
      case "description":
        return seed ?? transaction.description;
      case "group":
        return groupMap[transaction.group]?.id ?? (EMPTY_ID as GroupId);
      case "amount":
        return transaction.amount.amount;
      case "expenseType":
        return (
          expenseTypeMap[transaction.expenseType]?.id ??
          (EMPTY_ID as ExpenseTypeId)
        );
      case "datePaid":
        return transaction.datePaid;
      case "kid":
      case "payToAccount":
        return "";
    }
  };

  // Keyed by position, not by field: one rule can now ask for the same field
  // twice — once itself and once through a set it cites.
  const [values, setValues] = useState<PromptValue[]>(() =>
    prompts.map(initialValueFor),
  );

  const setValue = (index: number, value: PromptValue) =>
    setValues((prev) => prev.map((v, i) => (i === index ? value : v)));

  const handleConfirm = () => {
    const userActions = prompts.map((prompt, index) => {
      const action = buildAction(prompt.spec.field, values[index]);
      // Put the answer back under the guard it was found under, or a prompt
      // from inside a conditional would apply unconditionally.
      return prompt.wrap
        ? ({
            field: "conditional",
            value: { condition: prompt.wrap, action },
          } satisfies EditActionDto)
        : action;
    });
    onConfirm([
      { field: "applyAutoTx", value: rule.id as AutoTxId },
      ...userActions,
    ]);
  };

  const renderInput = (field: FieldName, index: number) => {
    const value = values[index];
    switch (field) {
      case "source":
        return (
          <SourcePickerField
            size="small"
            label={FIELD_LABELS[field]}
            value={(value as SourceId) || null}
            onChange={(newId) =>
              setValue(index, newId ?? (EMPTY_ID as SourceId))
            }
            fullWidth
          />
        );
      case "group":
        return (
          <GroupPickerField
            size="small"
            label={FIELD_LABELS[field]}
            value={(value as GroupId) || null}
            onChange={(newId) =>
              setValue(index, newId ?? (EMPTY_ID as GroupId))
            }
            fullWidth
          />
        );
      case "expenseType":
        return (
          <ExpenseTypePickerField
            size="small"
            label={FIELD_LABELS[field]}
            value={(value as ExpenseTypeId) || null}
            onChange={(newId) =>
              setValue(index, newId ?? (EMPTY_ID as ExpenseTypeId))
            }
            fullWidth
          />
        );
      case "amount":
        return (
          <TextField
            fullWidth
            size="small"
            type="number"
            value={value}
            onChange={(e) => setValue(index, e.target.value)}
            slotProps={{ htmlInput: { step: "0.01" } }}
          />
        );
      case "datePaid":
        return (
          <DatePicker
            mode="single"
            value={value as DateString}
            onChange={(v) => {
              if (v) setValue(index, makeDateString(v));
            }}
            fullWidth
          />
        );
      case "merchant":
      case "description":
        return (
          <TextField
            fullWidth
            size="small"
            autoFocus
            value={value as string}
            onFocus={caretToEnd}
            onChange={(e) => setValue(index, e.target.value)}
          />
        );
      case "kid":
      case "payToAccount":
        return null;
    }
  };

  return (
    <Dialog open onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>Apply rule: {rule.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            This rule asks for the following values before applying.
          </Typography>
          {prompts.map((prompt, index) => (
            <Box key={index}>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block", mb: 0.5 }}
              >
                {labels[index]}
              </Typography>
              {renderInput(prompt.spec.field, index)}
            </Box>
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button onClick={handleConfirm} variant="contained">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};
